import { FileUploadState } from 'components/ComposerMediaContext'
import { FileInfo } from 'components/FileInfo'
import FileUpload from 'components/FileUpload'
import { FileUploadProps, UploadHandle } from 'components/FileUpload/FileUpload'
import { memo, useCallback, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { MediaItemState } from 'services/compose/state'
import {
  MediaTileCallbacks,
  mediaElementId,
  mediaElementSelector,
  SortableWrapperProps,
  useFileInfoCallbacks,
  useSortableWrapperProps
} from './gridItem'
import styles from './MediaPreview.pcss'

export type FileGridItemProps = MediaTileCallbacks & {
  mediaItem: MediaItemState,
  file: FileUploadState,
  isFeatured: boolean,
  isVideo?: boolean,
  /** drag props from `useSortableWrapperProps` — omitted for items rendered outside the DndContext */
  wrapperProps?: SortableWrapperProps,
  onRemove: (fileName: string, fileId: string) => void
  onEdit: (fileId: string) => void
  onFileUploaded: (url: string, file: File, fileId: string) => void
  onUploadStarted: (upload: UploadHandle, fileId: string) => void
  onUploadError: (fileName: string, error: string, fileId: string) => void
  onExpand: (params: { url: string, selector: string, type: 'image' | 'video' }) => void
}

/** Tile for media the composer still holds as a local File — it drives (and displays) the upload. */
export const FileGridItem = memo(function FileGridItem(props: FileGridItemProps) {
  const { mediaItem, file, onRemove, onEdit, onFileUploaded, onUploadStarted, onUploadError, onExpand } = props
  const fileId = mediaItem.id
  const { metadataLoaded, sizeFetched } = useFileInfoCallbacks(fileId, props)

  // EXPL: built here rather than by the parent — an object literal in the grid's map would change on
  // every parent render and make the memo above useless.
  const fileUploadProps = useMemo<FileUploadProps<string>>(() => ({
    file: file.file,
    fileUrl: mediaItem.url,
    uploadId: fileId,
    rotateBy: '0',
    className: styles['no-border'],
    skipValidation: mediaItem.isEdited,
    loaderClassName: styles.hidden,
    onDelete: onRemove,
    onEdit,
    onFileUploaded,
    onUploadStarted,
    onError: onUploadError
  }), [file.file, fileId, mediaItem.url, mediaItem.isEdited, onRemove, onEdit, onFileUploaded, onUploadStarted, onUploadError])

  const handleExpand = useCallback((url: string, type: 'image' | 'video') => {
    onExpand({ url, selector: mediaElementSelector(fileId), type })
  }, [onExpand, fileId])

  return (
    <div className={styles['file-box-wrapper']} data-testid="file-grid-item" {...props.wrapperProps}>
      {props.isFeatured && (
        <div className={styles['label-featured']}>
          <FormattedMessage id="label.generic.featured" />
        </div>
      )}
      <div className={`${styles['file-box']} ${props.isVideo ? styles['video-file'] : ''}`}>
        <FileUpload
          {...fileUploadProps}
          abortOnUnmount
          mediaElementId={mediaElementId(fileId)}
          hideExpandButton
          expandOnContentClick
          onExpand={handleExpand}
        />
      </div>
      <FileInfo
        file={file.file}
        className={styles['file-box-footer']}
        onMetadataLoaded={metadataLoaded}
        onSizeFetched={sizeFetched}
      />
    </div>
  )
})

export const SortableFileGridItem = memo(function SortableFileGridItem(props: Omit<FileGridItemProps, 'wrapperProps'>) {
  const wrapperProps = useSortableWrapperProps(props.mediaItem.id)
  return <FileGridItem {...props} wrapperProps={wrapperProps} />
})
